"use client";

import { useState, useEffect, useRef } from "react";
import ContentList from "./ContentList";

import { NavigationProps, NavItem } from "./types";

const LeftContentList = ({ title, mainNavItems }: NavigationProps) => {
  const [selectedId, setSelectedId] = useState("1");
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isClickScrollingRef = useRef(false);
  const scrollAnimationRef = useRef<number | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (isClickScrollingRef.current) return;

        const visibleEntry = entries.reduce((prev, current) => {
          return current.intersectionRatio > prev.intersectionRatio
            ? current
            : prev;
        });

        if (visibleEntry.isIntersecting) {
          const sectionId = visibleEntry.target.id;
          const matchingItem = mainNavItems.find(
            (item) => item.sectionId === sectionId,
          );
          if (matchingItem) {
            setSelectedId(matchingItem.id as string);
          }
        }
      },
      {
        rootMargin: "-20% 0px -70% 0px",
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      },
    );

    mainNavItems.forEach((item) => {
      const element = document.getElementById(item.sectionId as string);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, [mainNavItems]);

  const smoothScrollTo = (targetPosition: number, duration: number = 800) => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }

    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();

    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);

      window.scrollTo(0, startPosition + distance * easeProgress);

      if (progress < 1) {
        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
      } else {
        scrollAnimationRef.current = null;
        isClickScrollingRef.current = false;
      }
    };

    scrollAnimationRef.current = requestAnimationFrame(animateScroll);
  };

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      // Get the element's position with 210px scroll margin
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 210;

      smoothScrollTo(offsetPosition);
    }
  };

  const handleItemSelect = (item: NavItem) => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }

    isClickScrollingRef.current = true;

    setSelectedId(item.id as string);
    scrollToElement(item.sectionId as string);
  };

  return (
    <>
      <div className="sticky top-[252px] hidden md:block">
        <ContentList
          title={title}
          items={mainNavItems}
          selectedId={selectedId}
          onItemSelect={handleItemSelect}
        />
      </div>
    </>
  );
};

export default LeftContentList;
